// src/components/reviewer/ReviewerControlPills.tsx
import React from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { domainTheme, type DomainCode } from "@/lib/domainTheme";
import {
  REVIEWER_CONTROL_GUIDANCE,
  type ReviewerControlGuidance,
} from "@/configs/controlGuidance/reviewerControlGuidance";

/* ── Hover content ── */
function GuidanceContent({ g }: { g: ReviewerControlGuidance }) {
  return (
    <div className="max-w-sm space-y-3">
      <div className="text-sm font-semibold">{g.title}</div>
      <p className="text-xs text-muted-foreground">{g.reviewer_guidance}</p>

      <Section heading="Evidence to Review" items={g.evidence_to_review} />
      <Section heading="Indicators of Strong Implementation" items={g.strong_indicators} />
      <Section heading="Common Gaps" items={g.common_gaps} />
    </div>
  );
}

function Section({ heading, items }: { heading: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <div className="text-xs">
      <h4 className="font-bold mt-2 mb-1">{heading}</h4>
      <ul className="list-disc pl-4 space-y-0.5">
        {items.map((t, i) => (
          <li key={i}>{t}</li>
        ))}
      </ul>
    </div>
  );
}

/* ── Pill list ── */
export function ReviewerControlPills({
  controls,
  currentDomain,
}: {
  controls: string[];
  currentDomain?: string;
}) {
  if (!Array.isArray(controls) || controls.length === 0) return null;

  const validDomains = ["GV", "ID", "PR", "DE", "RS", "RC"] as const;
  const domainKey =
    currentDomain && (validDomains as readonly string[]).includes(currentDomain)
      ? (currentDomain as DomainCode)
      : null;

  const pillClasses = domainKey
    ? domainTheme[domainKey].pill
    : "bg-slate-100 text-slate-700 ring-slate-200";

  return (
    <div className="mt-2 mb-1 max-w-full overflow-hidden">
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Mapped Controls
      </div>
      <div className="flex flex-wrap gap-2 max-w-full">
        {controls.map((code) => {
          const guidance = REVIEWER_CONTROL_GUIDANCE[code];

          if (!guidance) {
            // No reviewer guidance — render plain pill
            return (
              <span
                key={code}
                className={`inline-flex items-center rounded-full px-2 py-1 text-xs border ring-1 ring-inset select-none ${pillClasses}`}
              >
                {code}
              </span>
            );
          }

          return (
            <HoverCard key={code} openDelay={150}>
              <HoverCardTrigger asChild>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-1 text-xs border ring-1 ring-inset select-none cursor-help ${pillClasses}`}
                  tabIndex={0}
                  role="button"
                  aria-label={`${code} reviewer guidance`}
                >
                  {code}
                </span>
              </HoverCardTrigger>
              <HoverCardContent
                side="right"
                align="start"
                sideOffset={8}
                className="z-[9999] text-foreground"
              >
                <GuidanceContent g={guidance} />
              </HoverCardContent>
            </HoverCard>
          );
        })}
      </div>
    </div>
  );
}
